import { createRoot } from 'react-dom/client';

// Renders a React element (the same shared Letterhead components the Billing
// Settings live preview uses) into a new browser tab and hands it straight to
// the browser's own print pipeline — the same "Save as PDF" flow every
// browser already has, instead of rasterizing the DOM into a giant PNG and
// wrapping that image in jsPDF.
//
// That rasterize-then-wrap approach is what caused an earlier 274-page, 26MB
// PDF regression (see Finance.js history), and it's fundamentally the wrong
// tool here: a tall document (many rows, long text) produces a very tall
// off-screen canvas, and different browsers' canvas/PDF-page-size limits
// disagree on how to handle that — sometimes silently splitting or
// re-tiling it into dozens of oversized pages, and always turning crisp
// text into a blurry embedded image (which is also why the file balloons to
// tens of MB — a screenshot of text is a much bigger PDF payload than the
// text itself). Printing the real DOM lets the browser's print engine lay it
// out onto proper A4 pages with real, selectable, small-file-size text —
// exactly what the "Save as PDF" print-dialog option already does well.
// The print dialog itself is what gives the user the "save wherever I want"
// choice; there's no separate download step to automate around that.
export function printComponent(element, docTitle) {
  return new Promise((resolve, reject) => {
    const printWin = window.open('', '_blank');
    if (!printWin) { reject(new Error('Pop-up blocked — please allow pop-ups for this site and try again.')); return; }
    printWin.document.write(`<!DOCTYPE html><html><head><title>${docTitle}</title>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @page { size: A4; margin: 10mm; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        html, body { margin: 0; padding: 0; }
      </style>
    </head><body><div id="print-root"></div></body></html>`);
    printWin.document.close();

    const mountAndPrint = () => {
      const container = printWin.document.getElementById('print-root');
      const root = createRoot(container);
      root.render(element);
      // Give fonts/images a moment to load before invoking print — the
      // window's own onload already waited for the shell document, not for
      // the React tree we just mounted into it.
      setTimeout(() => {
        printWin.focus();
        printWin.print();
        resolve();
      }, 450);
    };

    if (printWin.document.readyState === 'complete') mountAndPrint();
    else printWin.onload = mountAndPrint;
  });
}
